import React, { useState } from "react";
import "./Accounts.css";
import "../Dashboard/Dashboard.css"; // dashHead / sx-grain / sx-gradients live here
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { passwordReset } from "../../Redux/actions/authActions";
import Navbar_acc from "../../UiComponents/Navbar_acc";
import { toast, ToastContainer } from "react-toastify";
import CustomToast, { customToatStyle } from "../../Components/CustomToast/CustomToast";
import { FaEye, FaEyeSlash } from "react-icons/fa";

export default function AccountSettings() {
  const userInfo = JSON.parse(localStorage.getItem("user"));
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [show, setShow] = useState({ current: false, new: false, confirm: false });

  const fullName = `${userInfo?.firstName || ""} ${userInfo?.lastName || ""}`.trim();

  const CloseButton = ({ closeToast }) => (
    <i
      className="material-icons w-7 flex justify-center"
      onClick={closeToast}
      style={{ fontStyle: "normal" }}
    >
      ✖
    </i>
  );

  const submitHandler = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast(
        <CustomToast title="" content="New passwords do not match" />,
        customToatStyle
      );
      return;
    }

    setIsChangingPassword(true);
    const res = await dispatch(
      passwordReset(currentPassword, newPassword, confirmPassword)
    );

    if (res?.status) {
      setIsChangingPassword(false);
      toast(
        <CustomToast title="" content={res?.data?.message} />,
        customToatStyle
      );
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setTimeout(() => {
        navigate("/dashboard");
      }, 9000);
    } else {
      setIsChangingPassword(false);
      setTimeout(() => {
        toast(
          <CustomToast
            title=""
            content={res?.response?.data?.message?.slice(6)}
          />,
          customToatStyle
        );
      }, 100);
    }
  };

  // Shared input styling for the editable password fields
  const inputClass =
    "w-full bg-[rgba(255,255,255,.04)] border-[1px] border-[rgba(255,255,255,.08)] rounded-xl px-4 py-3 pr-11 text-white placeholder:text-[#625e83] focus:outline-none focus:border-[#7c5cff] transition-colors";
  const eyeBtnClass =
    "absolute right-3 top-1/2 -translate-y-1/2 text-[#625e83] hover:text-white transition-colors";

  return (
    <div className="h-screen w-full sx-grain bg-[#06050c] sx-gradients flex flex-col overflow-hidden">
      {/* Animated particle background (replaces the reference <PageShell />) */}
      <div className="particles"></div>

      <ToastContainer closeButton={CloseButton} />

      {/* Fixed header */}
      <div className="dashHead shrink-0">
        <Navbar_acc />
      </div>

      {/* Scrollable content area */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-2xl w-full mx-auto px-6 py-12">
          <h1 className="text-3xl font-bold text-white mb-8">My Account</h1>

          {/* Account Information */}
          <div className="bg-[rgba(255,255,255,.02)] border-[1px] border-[rgba(255,255,255,.06)] rounded-2xl p-6 md:p-8 mb-8">
            <h2 className="text-lg font-semibold text-white mb-6">
              Account Information
            </h2>
            <div className="space-y-6">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-[#a8a4c8]">
                  Full Name
                </label>
                <input
                  type="text"
                  value={fullName}
                  readOnly
                  disabled
                  className="w-full bg-[rgba(255,255,255,.02)] border-[1px] border-[rgba(255,255,255,.04)] rounded-xl px-4 py-3 text-[#625e83] cursor-not-allowed"
                />
                <p className="text-xs text-[#625e83]">
                  Full name cannot be changed.
                </p>
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-[#a8a4c8]">
                  Email Address
                </label>
                <input
                  type="email"
                  value={userInfo?.email || ""}
                  readOnly
                  disabled
                  className="w-full bg-[rgba(255,255,255,.02)] border-[1px] border-[rgba(255,255,255,.04)] rounded-xl px-4 py-3 text-[#625e83] cursor-not-allowed"
                />
                <p className="text-xs text-[#625e83]">
                  Email address cannot be changed.
                </p>
              </div>
            </div>
          </div>

          {/* Change Password */}
          <div className="bg-[rgba(255,255,255,.02)] border-[1px] border-[rgba(255,255,255,.06)] rounded-2xl p-6 md:p-8">
            <h2 className="text-lg font-semibold text-white mb-6">
              Change Password
            </h2>
            <form onSubmit={submitHandler} className="space-y-5">
              {/* Current Password */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-[#a8a4c8]">
                  Current Password
                </label>
                <div className="relative">
                  <input
                    type={show.current ? "text" : "password"}
                    required
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className={inputClass}
                  />
                  <button
                    type="button"
                    onClick={() => setShow((s) => ({ ...s, current: !s.current }))}
                    className={eyeBtnClass}
                    aria-label="Toggle current password visibility"
                  >
                    {show.current ? <FaEye /> : <FaEyeSlash />}
                  </button>
                </div>
              </div>

              {/* New Password */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-[#a8a4c8]">
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={show.new ? "text" : "password"}
                    required
                    minLength={8}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className={inputClass}
                  />
                  <button
                    type="button"
                    onClick={() => setShow((s) => ({ ...s, new: !s.new }))}
                    className={eyeBtnClass}
                    aria-label="Toggle new password visibility"
                  >
                    {show.new ? <FaEye /> : <FaEyeSlash />}
                  </button>
                </div>
              </div>

              {/* Confirm New Password */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-[#a8a4c8]">
                  Confirm New Password
                </label>
                <div className="relative">
                  <input
                    type={show.confirm ? "text" : "password"}
                    required
                    minLength={8}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className={inputClass}
                  />
                  <button
                    type="button"
                    onClick={() => setShow((s) => ({ ...s, confirm: !s.confirm }))}
                    className={eyeBtnClass}
                    aria-label="Toggle confirm password visibility"
                  >
                    {show.confirm ? <FaEye /> : <FaEyeSlash />}
                  </button>
                </div>
              </div>

              <div className="pt-4 flex items-center justify-end gap-3 border-t-[1px] border-[rgba(255,255,255,.06)] mt-6">
                <button
                  type="button"
                  onClick={() => navigate("/dashboard")}
                  className="px-5 py-2.5 rounded-lg text-sm font-semibold text-[#a8a4c8] border-[1px] border-[rgba(255,255,255,.12)] hover:text-white hover:border-[rgba(255,255,255,.24)] transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={
                    isChangingPassword ||
                    !currentPassword ||
                    !newPassword ||
                    !confirmPassword
                  }
                  className="px-5 py-2.5 rounded-lg text-sm font-semibold bg-[#7c5cff] hover:bg-[#6b4ce6] text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isChangingPassword ? "Updating..." : "Update Password"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}